import { useEffect, useState, useMemo, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { adminApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import DataTable from '../../components/ui/DataTable.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Select from '../../components/ui/Select.jsx';
import Modal from '../../components/ui/Modal.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { Card, CardContent } from '../../components/ui/Card.jsx';
import { Settings2, Plus, Trash2, AlertTriangle, UserPlus, HelpCircle, X, Search, Loader2, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCurrentUser } from '../../hooks/useCurrentUser.jsx';
import { BANK_LOCATIONS } from '../../lib/bankLocations.js';

const SETUP_HELP = {
  'Role':              'Click this row → Role dropdown',
  'Department':        'Click this row → Department field',
  'Manager':           'Click this row → Manager dropdown',
  'Team':              'Click this row → Team dropdown',
  'Hire date':         'Click this row → Hire Date field',
  'Contract':          'Contracts page → upload contract file',
  'Leave entitlement': 'Contracts page → click row → Annual Entitlement',
};

function NeedsSetupBadge({ missing }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const btnRef = useRef(null);

  // Recompute popover position any time it opens or the page scrolls/resizes.
  // This popover MUST portal to document.body — the parent DataTable wrapper
  // has overflow-hidden + overflow-x-auto, which clips an absolute-positioned
  // popover to the cell. Fixed positioning from a body portal escapes that.
  useLayoutEffect(() => {
    if (!open) return;
    function update() {
      const rect = btnRef.current?.getBoundingClientRect();
      if (!rect) return;
      const popoverWidth = 320;
      // Align popover to right edge of the badge; clamp to viewport.
      const left = Math.max(8, Math.min(rect.right - popoverWidth, window.innerWidth - popoverWidth - 8));
      const top = rect.bottom + 6;
      setCoords({ top, left });
    }
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  return (
    <span className="relative inline-block">
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((s) => !s); }}
        className="cursor-pointer"
      >
        <Badge variant="warning">Needs Setup ({missing.length})</Badge>
      </button>
      {open && coords && createPortal(
        <>
          <div
            className="fixed inset-0 z-[1000]"
            onClick={(e) => { e.stopPropagation(); setOpen(false); }}
          />
          <div
            className="fixed z-[1001] w-[320px] rounded-lg border border-border bg-card p-3 shadow-lg text-left"
            style={{ top: coords.top, left: coords.left }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-warning" />
              <span className="text-sm font-semibold text-foreground">Missing configuration</span>
            </div>
            <ul className="space-y-2 text-xs">
              {missing.map((m) => (
                <li key={m} className="flex flex-col gap-0.5">
                  <span className="font-medium text-foreground">{m}</span>
                  <span className="text-muted-foreground">{SETUP_HELP[m] || '—'}</span>
                </li>
              ))}
            </ul>
          </div>
        </>,
        document.body
      )}
    </span>
  );
}

function getConfigStatus(user) {
  const missing = [];
  if (!user.role) missing.push('Role');
  if (!user.department) missing.push('Department');
  if (!user.managerId) missing.push('Manager');
  if (!user.teamId) missing.push('Team');
  if (!user.hireDate) missing.push('Hire date');
  if (!user.contractBlobPath) missing.push('Contract');
  if (!user.annualLeaveEntitlement) missing.push('Leave entitlement');
  return { complete: missing.length === 0, missing };
}

const ROLES = [
  { value: 'employee', label: 'Employee' },
  { value: 'approver', label: 'Approver' },
  { value: 'finance', label: 'Finance' },
  { value: 'admin', label: 'Admin' },
];

// Mirrors api/shared/employeeLevels.js — keep the two in step.
const LEVEL_OPTIONS = [
  { value: '', label: '— not set —' },
  { value: 'staff', label: 'Staff' },
  { value: 'senior', label: 'Senior' },
  { value: 'manager', label: 'Manager' },
  { value: 'director', label: 'Director' },
  { value: 'executive', label: 'Executive' },
];

const EMPLOYMENT_TYPES = [
  { value: 'full_time', label: 'Full-time' },
  { value: 'part_time', label: 'Part-time' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'intern', label: 'Intern' },
];

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Bank Location options for the dropdown — labeled by country, valued by
// the 3-letter home-currency code stored on the user (bankCurrency).
const BANK_LOCATION_OPTIONS = BANK_LOCATIONS.map((b) => ({
  value: b.currency,
  label: `${b.label} (${b.currency})`,
}));

// user.workingDays can arrive as a JSON string, an array, or null
// (use global default). Parse to a Set<number> so toggling is cheap.
function parseWorkingDays(raw) {
  if (Array.isArray(raw)) return new Set(raw.map(Number).filter((n) => Number.isFinite(n)));
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) return new Set(arr.map(Number).filter((n) => Number.isFinite(n)));
    } catch {}
  }
  return null;
}

export default function UsersPage() {
  const toast = useToast();
  const { user: currentUser } = useCurrentUser();
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all');
  const [editingUser, setEditingUser] = useState(null);
  const [showTeamsModal, setShowTeamsModal] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [roles, setRoles] = useState([]);

  async function load() {
    setLoading(true);
    try {
      const [usersRes, teamsRes, rolesRes] = await Promise.all([
        adminApi.listUsers(),
        adminApi.listTeams().catch(() => ({ teams: [] })),
        adminApi.listRoles().catch(() => ({ roles: [] })),
      ]);
      setUsers(usersRes.users || []);
      setTeams(teamsRes.teams || []);
      setRoles(rolesRes.roles || []);
    } catch (err) {
      toast.error(`Failed to load users: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const currentUserKey = currentUser?.rowKey || currentUser?.email;
  const isRowSelectable = (u) => u.rowKey !== currentUserKey;

  async function handleBulkDelete() {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const names = ids
      .map((id) => users.find((u) => u.rowKey === id))
      .filter(Boolean)
      .map((u) => u.displayName || u.email)
      .slice(0, 5);
    const preview = names.join(', ') + (ids.length > names.length ? `, +${ids.length - names.length} more` : '');
    const ok = window.confirm(
      `Remove ${ids.length} user${ids.length !== 1 ? 's' : ''} from KSA ProHR?\n\n${preview}\n\nThey will lose access immediately and cannot sign in again unless re-invited. Historical leave requests and balances remain intact.`,
    );
    if (!ok) return;
    setBulkDeleting(true);
    try {
      const res = await adminApi.bulkDeleteUsers(ids);
      toast.success(
        `Removed ${res.deletedCount || 0} user${(res.deletedCount || 0) !== 1 ? 's' : ''}${
          res.skippedSelfCount ? ' (skipped yourself)' : ''
        }`,
      );
      setSelected(new Set());
      await load();
    } catch (err) {
      toast.error(`Bulk delete failed: ${err.message}`);
    } finally {
      setBulkDeleting(false);
    }
  }

  const activeTeams = teams.filter((t) => t.isActive !== false);
  const getTeamName = (teamId) => teams.find((t) => t.rowKey === teamId)?.displayName || '';

  const filtered = useMemo(() => {
    let list = users;
    if (filter === 'active') list = list.filter((u) => u.isActive !== false);
    else if (filter === 'inactive') list = list.filter((u) => u.isActive === false);
    else if (filter === 'needs_setup') list = list.filter((u) => u.isActive !== false && !getConfigStatus(u).complete);
    else if (filter.startsWith('role:')) { const key = filter.slice(5); list = list.filter((u) => (u.role || 'employee') === key); }

    if (teamFilter !== 'all') {
      if (teamFilter === 'unassigned') {
        list = list.filter((u) => !u.teamId);
      } else {
        list = list.filter((u) => u.teamId === teamFilter);
      }
    }

    if (search) {
      const s = search.toLowerCase();
      list = list.filter(
        (u) =>
          u.displayName?.toLowerCase().includes(s) ||
          u.email?.toLowerCase().includes(s) ||
          u.department?.toLowerCase().includes(s)
      );
    }
    return list.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
  }, [users, search, filter, teamFilter]);

  const columns = [
    {
      key: 'displayName',
      label: 'Name',
      render: (u) => (
        <div>
          <div className="font-medium">{u.displayName || u.email}</div>
          <div className="text-xs text-muted-foreground">{u.email}</div>
        </div>
      ),
    },
    {
      key: 'teamId',
      label: 'Team',
      render: (u) => {
        const name = getTeamName(u.teamId);
        return name ? (
          <Badge variant="default">{name}</Badge>
        ) : (
          <span className="text-muted-foreground/40">—</span>
        );
      },
    },
    { key: 'department', label: 'Department', render: (u) => u.department || '—' },
    {
      key: 'role',
      label: 'Role',
      render: (u) => (
        <Badge variant={u.role === 'admin' ? 'info' : u.role === 'approver' ? 'warning' : 'default'}>
          {u.role || 'employee'}
        </Badge>
      ),
    },
    {
      key: 'isActive',
      label: 'Status',
      render: (u) => (
        <Badge variant={u.isActive === false ? 'danger' : 'success'}>
          {u.isActive === false ? 'Inactive' : 'Active'}
        </Badge>
      ),
    },
    {
      key: 'config',
      label: 'Config',
      render: (u) => {
        if (u.isActive === false) return '—';
        const { complete, missing } = getConfigStatus(u);
        return complete ? (
          <Badge variant="success">Configured</Badge>
        ) : (
          <NeedsSetupBadge missing={missing} />
        );
      },
    },
    { key: 'hireDate', label: 'Hire Date', render: (u) => u.hireDate || '—' },
  ];

  return (
    <div>
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight mb-2">People</h1>
          <p className="text-muted-foreground">
            {users.length} total · {users.filter((u) => u.isActive !== false).length} active
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowInvite(true)} variant="default">
            <UserPlus className="h-4 w-4" /> Invite Employee
          </Button>
          <div className="relative">
            <button
              onClick={() => setShowHelp((s) => !s)}
              className={cn(
                'inline-flex h-9 w-9 items-center justify-center rounded-full border transition-colors',
                showHelp
                  ? 'border-primary/50 bg-primary/10 text-primary'
                  : 'border-border bg-card text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
              title="How to manage people"
              aria-label="How to manage people"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
            {showHelp && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowHelp(false)} />
                <div className="absolute right-0 top-full z-40 mt-1 w-[340px] rounded-lg border border-border bg-card p-3 shadow-lg">
                  <div className="mb-2 flex items-center gap-2">
                    <HelpCircle className="h-4 w-4 text-primary" />
                    <span className="text-sm font-semibold text-foreground">How to manage people</span>
                  </div>
                  <ol className="space-y-2 text-xs text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="shrink-0 font-bold text-foreground">1.</span>
                      <span>Click any <span className="text-foreground font-medium">row</span> to edit role, team, department, manager, hire date, and employment type.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="shrink-0 font-bold text-foreground">2.</span>
                      <span><span className="text-foreground font-medium">Invite Employee</span> opens a search across your Microsoft 365 directory — pick anyone in the org or paste an external email and send them a registration link.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="shrink-0 font-bold text-foreground">3.</span>
                      <span>A yellow <span className="text-warning font-medium">Needs Setup</span> badge means required fields are missing — click it to see which fields and where to fix them.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="shrink-0 font-bold text-foreground">4.</span>
                      <span>Tick rows and click <span className="text-destructive font-medium">Remove from KSA ProHR</span> to revoke access for multiple people at once. Their history stays for records — re-invite them if you need them back.</span>
                    </li>
                  </ol>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {(() => {
        const needsSetup = users.filter((u) => u.isActive !== false && !getConfigStatus(u).complete).length;
        if (needsSetup === 0) return null;
        return (
          <Card className="mb-5 border-warning/30 bg-warning/5">
            <CardContent className="flex items-center gap-3 py-3">
              <AlertTriangle className="h-4 w-4 text-warning shrink-0" />
              <p className="text-sm text-warning font-medium">{needsSetup} employee{needsSetup !== 1 ? 's' : ''} need{needsSetup === 1 ? 's' : ''} configuration</p>
              <button onClick={() => setFilter('needs_setup')} className="ml-auto text-xs text-primary hover:underline cursor-pointer">Show them</button>
            </CardContent>
          </Card>
        );
      })()}

      <div className="flex gap-3 mb-5 items-end flex-wrap">
        <Input
          label="Search"
          placeholder="Name, email, or department…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 max-w-[400px]"
        />
        <Select
          label="Filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          options={[
            { value: 'all', label: 'All users' },
            { value: 'active', label: 'Active only' },
            { value: 'inactive', label: 'Inactive only' },
            { value: 'needs_setup', label: 'Needs Setup' },
            // One entry per configured role (incl. custom roles), so you can
            // filter People by any role — not just the old built-ins.
            ...roles.map((r) => ({ value: `role:${r.key}`, label: `Role: ${r.displayName || r.key}` })),
          ]}
        />
        <Select
          label="Team"
          value={teamFilter}
          onChange={(e) => setTeamFilter(e.target.value)}
          options={[
            { value: 'all', label: 'All teams' },
            { value: 'unassigned', label: 'Unassigned' },
            ...activeTeams.map((t) => ({ value: t.rowKey, label: t.displayName })),
          ]}
        />
        <div className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium text-muted-foreground">&nbsp;</span>
          <Button variant="secondary" onClick={() => setShowTeamsModal(true)} className="h-10 text-[13px]">
            <Settings2 className="h-3.5 w-3.5" />
            Manage Teams
          </Button>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="mb-3 flex items-center justify-between rounded-lg border border-primary/30 bg-primary/10 px-4 py-2.5">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-foreground">
              {selected.size} selected
            </span>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-3.5 w-3.5" /> Clear
            </button>
          </div>
          <Button
            variant="danger"
            size="sm"
            onClick={handleBulkDelete}
            loading={bulkDeleting}
          >
            <Trash2 className="h-3.5 w-3.5" />
            Remove from KSA ProHR
          </Button>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={filtered}
        loading={loading}
        emptyMessage={
          users.length === 0
            ? 'No users yet. Click "Invite Employee" to add the first one.'
            : 'No users match your filters.'
        }
        onRowClick={(u) => setEditingUser(u)}
        selectable
        selectedKeys={selected}
        onSelectionChange={setSelected}
        isRowSelectable={isRowSelectable}
      />

      {editingUser && (
        <UserEditModal
          user={editingUser}
          users={users}
          teams={activeTeams}
          onClose={() => setEditingUser(null)}
          onSaved={async () => {
            setEditingUser(null);
            await load();
          }}
        />
      )}

      {showInvite && (
        <InviteModal
          users={users}
          teams={activeTeams}
          onClose={() => setShowInvite(false)}
          onSaved={async () => { setShowInvite(false); await load(); }}
        />
      )}

      {showTeamsModal && (
        <ManageTeamsModal
          teams={teams}
          onClose={() => setShowTeamsModal(false)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function UserEditModal({ user, users, teams, onClose, onSaved }) {
  const toast = useToast();
  // Role options include any custom roles created in Roles & Permissions,
  // not just the four built-ins, so they can be assigned here. Falls back to
  // the built-in ROLES if the fetch fails.
  const [roleOptions, setRoleOptions] = useState(ROLES);
  useEffect(() => {
    adminApi.listRoles()
      .then((res) => {
        const opts = (res.roles || []).map((r) => ({ value: r.key, label: r.displayName || r.key }));
        if (opts.length) setRoleOptions(opts);
      })
      .catch(() => { /* keep built-in ROLES */ });
  }, []);
  const [form, setForm] = useState({
    role: user.role || 'employee',
    level: user.level || '',
    department: user.department || '',
    managerId: user.managerId || '',
    chainId: user.chainId || '',
    hireDate: user.hireDate || '',
    birthday: user.birthday || '',
    employmentType: user.employmentType || 'full_time',
    isActive: user.isActive !== false,
    teamId: user.teamId || '',
    noManager: user.noManager === true,
    // Bank Location — resolves to the employee's home currency for the
    // expense foreign-transaction fee. Default to Saudi Arabia (SAR) when
    // unset so existing employees keep today's behavior until an admin
    // explicitly sets something else.
    bankCurrency: user.bankCurrency || 'SAR',
  });

  // null = "use global default working days". A non-null Set is a
  // per-user override (e.g. someone on a US schedule who works
  // Mon-Fri while the rest of the company is Sun-Thu).
  const [workingDays, setWorkingDays] = useState(() => parseWorkingDays(user.workingDays));
  const usesGlobalWorkingDays = workingDays === null;

  function toggleWorkingDay(day) {
    setWorkingDays((prev) => {
      const next = new Set(prev || []);
      if (next.has(day)) next.delete(day);
      else next.add(day);
      return next;
    });
  }
  const [saving, setSaving] = useState(false);
  const [chains, setChains] = useState([]);

  useEffect(() => {
    adminApi.listChains().then((res) => setChains(res.chains || [])).catch(() => {});
  }, []);

  const managerOptions = [
    { value: '', label: '— None —' },
    ...users
      .filter((u) => u.rowKey !== user.rowKey && u.isActive !== false)
      .map((u) => ({ value: u.rowKey, label: u.displayName || u.email })),
  ];

  const chainOptions = [
    { value: '', label: '— None —' },
    ...chains
      .filter((c) => c.isActive !== false)
      .map((c) => ({ value: c.rowKey, label: c.name || c.rowKey })),
  ];

  const teamOptions = [
    { value: '', label: '— No team —' },
    ...teams.map((t) => ({ value: t.rowKey, label: t.displayName })),
  ];

  async function handleSave() {
    setSaving(true);
    try {
      // Empty string clears the override so the user falls back to
      // the system default. Otherwise serialize the chosen weekdays
      // as a JSON-encoded sorted array of day-of-week numbers.
      const workingDaysPayload = workingDays === null
        ? ''
        : JSON.stringify([...workingDays].sort((a, b) => a - b));
      await adminApi.updateUser(user.rowKey, { ...form, workingDays: workingDaysPayload });
      toast.success(`Updated ${user.displayName || user.email}`);
      onSaved();
    } catch (err) {
      toast.error(`Update failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Edit ${user.displayName || user.email}`}
      width="560px"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving}>
            Save changes
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Role"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            options={roleOptions}
          />
          <Select
            label="Employment type"
            value={form.employmentType}
            onChange={(e) => setForm({ ...form, employmentType: e.target.value })}
            options={EMPLOYMENT_TYPES}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Department"
            value={form.department}
            onChange={(e) => setForm({ ...form, department: e.target.value })}
            placeholder="e.g. Engineering"
          />
          <Select
            label="Team"
            value={form.teamId}
            onChange={(e) => setForm({ ...form, teamId: e.target.value })}
            options={teamOptions}
          />
        </div>

        <div>
          <Select
            label="Level"
            value={form.level}
            onChange={(e) => setForm({ ...form, level: e.target.value })}
            options={LEVEL_OPTIONS}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Seniority, used by level-restricted leave types (e.g. Remote Work is Manager
            and above). Someone with no level set ranks below every level, so they will
            not qualify until this is filled in.
          </p>
        </div>

        <Select
          label="Bank Location"
          value={form.bankCurrency}
          onChange={(e) => setForm({ ...form, bankCurrency: e.target.value })}
          options={BANK_LOCATION_OPTIONS}
        />
        <p className="-mt-3 text-xs text-muted-foreground">
          Determines the employee's home currency for the 3% foreign transaction fee — expense lines in this currency are exempt; every other currency (including SAR) is charged the fee.
        </p>

        <Select
          label="Manager"
          value={form.managerId}
          onChange={(e) => setForm({ ...form, managerId: e.target.value })}
          options={managerOptions}
          disabled={form.noManager}
        />

        <label className="flex items-center gap-2.5 rounded-md border border-border bg-card p-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.noManager}
            onChange={(e) => setForm({ ...form, noManager: e.target.checked, managerId: e.target.checked ? '' : form.managerId })}
          />
          <div>
            <div className="font-medium">No manager</div>
            <div className="text-xs text-muted-foreground">
              For people who don't report to anyone (e.g. partners). Approval chains skip
              every Direct Manager step for this user — leave and expense requests advance
              straight to the next step in the chain.
            </div>
          </div>
        </label>

        <Select
          label="Leave Approval Chain"
          value={form.chainId}
          onChange={(e) => setForm({ ...form, chainId: e.target.value })}
          options={chainOptions}
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Hire date"
            type="date"
            value={form.hireDate}
            onChange={(e) => setForm({ ...form, hireDate: e.target.value })}
          />
          <Input
            label="Birthday"
            type="date"
            max={new Date().toISOString().slice(0, 10)}
            value={form.birthday}
            onChange={(e) => setForm({ ...form, birthday: e.target.value })}
          />
        </div>
        <p className="-mt-3 text-xs text-muted-foreground">
          Shown on the Team Calendar as a birthday each year. Colleagues see the day and
          month only — the year is never shared. Leave blank to keep it off the calendar.
        </p>

        {/* Per-user working days override. Used for the rare case where
            an employee works a different schedule (e.g. someone on a US
            workweek while the rest of the company is on Sun-Thu KSA).
            Leave balance + planner both read from this — Fri/Sat marked
            here count as workdays for that user. */}
        <div className="rounded-md border border-border bg-card p-3">
          <div className="mb-2 flex items-center justify-between">
            <div>
              <div className="text-sm font-medium">Working days</div>
              <div className="text-xs text-muted-foreground">
                {usesGlobalWorkingDays
                  ? 'Using the company-wide default from Settings.'
                  : 'Custom schedule — overrides the company default.'}
              </div>
            </div>
            <button
              type="button"
              onClick={() =>
                setWorkingDays((prev) => (prev === null ? new Set([0, 1, 2, 3, 4]) : null))
              }
              className="text-xs text-primary hover:underline"
            >
              {usesGlobalWorkingDays ? 'Customize' : 'Use default'}
            </button>
          </div>
          {!usesGlobalWorkingDays && (
            <div className="flex flex-wrap gap-2">
              {WEEKDAY_LABELS.map((label, i) => {
                const active = workingDays.has(i);
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => toggleWorkingDay(i)}
                    className={cn(
                      'rounded-md border px-3 py-1.5 text-xs transition-colors',
                      active
                        ? 'border-primary bg-primary/10 font-semibold text-primary'
                        : 'border-input bg-background text-muted-foreground hover:bg-muted',
                    )}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <label className="flex items-center gap-2.5 rounded-md border border-border bg-card p-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
          />
          <div>
            <div className="font-medium">Active</div>
            <div className="text-xs text-muted-foreground">
              Inactive users cannot sign in and are locked out of the system.
            </div>
          </div>
        </label>
      </div>
    </Modal>
  );
}

/**
 * Searchable combobox that queries Microsoft Graph live for tenant users.
 * Falls back gracefully to manual email entry if Graph search fails — some
 * tenants may not have the permission granted yet, and we don't want to
 * block invites on that. Debounced 250ms, min 2 chars.
 */
function GraphUserPicker({ value, displayName, onPick, onClear, onEmailChange, onDisplayNameChange }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [manualMode, setManualMode] = useState(false);

  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    setError(null);
    const handle = setTimeout(async () => {
      try {
        const res = await adminApi.searchGraphUsers(query.trim());
        setResults(res.users || []);
      } catch (err) {
        setError(err.message || 'Graph search unavailable');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query]);

  // If a user has been picked (value set), show the locked-in card. Otherwise show searcher.
  if (value && !manualMode) {
    return (
      <div className="rounded-md border border-primary/40 bg-primary/5 p-3 flex items-start gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/15 text-primary shrink-0">
          <Check className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-foreground truncate">{displayName || value}</div>
          <div className="text-xs text-muted-foreground truncate">{value}</div>
        </div>
        <button
          type="button"
          onClick={() => { onClear(); setQuery(''); setResults([]); }}
          className="text-xs text-muted-foreground hover:text-destructive transition-colors shrink-0"
        >
          Change
        </button>
      </div>
    );
  }

  if (manualMode) {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-4">
          <Input label="Email *" type="email" value={value} onChange={(e) => onEmailChange(e.target.value)} placeholder="jane@company.com" />
          <Input label="Display Name" value={displayName} onChange={(e) => onDisplayNameChange(e.target.value)} placeholder="Jane Smith" />
        </div>
        <button
          type="button"
          onClick={() => setManualMode(false)}
          className="text-xs text-primary hover:underline"
        >
          ← Back to directory search
        </button>
      </div>
    );
  }

  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-foreground">
        Find user in Microsoft 365 directory *
      </label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search by name or email (min 2 chars)..."
          className="w-full bg-card border border-border rounded-lg pl-10 pr-10 py-2.5 text-sm focus:border-primary/40 focus:outline-none transition-colors placeholder:text-muted-foreground/40"
          autoFocus
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />}
      </div>

      {open && query.trim().length >= 2 && (
        <div className="mt-1.5 rounded-md border border-border bg-card max-h-72 overflow-y-auto shadow-lg">
          {loading && results.length === 0 && (
            <div className="p-3 text-xs text-muted-foreground text-center">Searching directory...</div>
          )}
          {!loading && results.length === 0 && !error && (
            <div className="p-3 text-xs text-muted-foreground text-center">No matches in your Microsoft 365 directory.</div>
          )}
          {error && (
            <div className="p-3 text-xs text-destructive text-center">
              Directory search failed: {error}
            </div>
          )}
          {results.map((u) => {
            const disabled = u.existingStatus === 'active';
            return (
              <button
                key={u.id}
                type="button"
                disabled={disabled}
                onClick={() => {
                  if (disabled) return;
                  onPick(u);
                  setOpen(false);
                  setQuery('');
                }}
                className={cn(
                  'w-full text-left px-3 py-2.5 border-b border-border last:border-0 transition-colors',
                  disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-muted/50 cursor-pointer'
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-foreground truncate">{u.displayName}</div>
                    <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                    {(u.jobTitle || u.department) && (
                      <div className="text-xs text-muted-foreground/70 truncate mt-0.5">
                        {[u.jobTitle, u.department].filter(Boolean).join(' • ')}
                      </div>
                    )}
                  </div>
                  {u.existingStatus === 'active' && <Badge variant="default" className="shrink-0">In KSA ProHR</Badge>}
                  {u.existingStatus === 'deleted' && <Badge variant="warning" className="shrink-0">Will reactivate</Badge>}
                </div>
              </button>
            );
          })}
        </div>
      )}

      <div className="mt-2 flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Searches your tenant via Microsoft Graph.
        </p>
        <button
          type="button"
          onClick={() => setManualMode(true)}
          className="text-xs text-primary hover:underline"
        >
          Enter email manually →
        </button>
      </div>
    </div>
  );
}

function InviteModal({ users, teams, onClose, onSaved }) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    email: '', displayName: '', role: 'employee', department: '', managerId: '',
    teamId: '', chainId: '', hireDate: '', birthday: '', employmentType: 'full_time',
    annualLeaveEntitlement: '', officeLocation: '', employeeId: '',
  });
  const [chains, setChains] = useState([]);

  useEffect(() => {
    adminApi.listChains().then((res) => setChains(res.chains || [])).catch(() => {});
  }, []);

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const managerOptions = [
    { value: '', label: '— None —' },
    ...users.filter((u) => u.isActive !== false).map((u) => ({ value: u.rowKey, label: u.displayName || u.email })),
  ];
  const teamOptions = [
    { value: '', label: '— No team —' },
    ...teams.map((t) => ({ value: t.rowKey, label: t.displayName })),
  ];
  const chainOptions = [
    { value: '', label: '— None (use default) —' },
    ...chains
      .filter((c) => c.isActive !== false)
      .map((c) => ({ value: c.rowKey, label: c.name || c.rowKey })),
  ];

  async function handleInvite() {
    if (!form.email || !form.email.includes('@')) {
      toast.error('Valid email address is required');
      return;
    }
    setSaving(true);
    try {
      const res = await adminApi.inviteUser(form);
      toast.success(`Invited ${form.email}${res.emailSent ? ' — email sent' : ' (email not configured)'}`);
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal isOpen onClose={onClose} title="Invite Employee" width="600px"
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={handleInvite} loading={saving}><UserPlus className="h-4 w-4" /> Send Invite</Button></>}>
      <div className="flex flex-col gap-4">
        <GraphUserPicker
          value={form.email}
          displayName={form.displayName}
          onPick={(u) => setForm((f) => ({
            ...f,
            email: u.email || '',
            displayName: u.displayName || f.displayName,
            department: u.department || f.department,
          }))}
          onClear={() => setForm((f) => ({ ...f, email: '', displayName: '' }))}
          onEmailChange={(v) => set('email', v)}
          onDisplayNameChange={(v) => set('displayName', v)}
        />
        <div className="grid grid-cols-2 gap-4">
          <Select label="Role" value={form.role} onChange={(e) => set('role', e.target.value)} options={ROLES} />
          <Select label="Employment Type" value={form.employmentType} onChange={(e) => set('employmentType', e.target.value)} options={EMPLOYMENT_TYPES} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input label="Department" value={form.department} onChange={(e) => set('department', e.target.value)} placeholder="e.g. Engineering" />
          <Select label="Team" value={form.teamId} onChange={(e) => set('teamId', e.target.value)} options={teamOptions} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Select label="Manager" value={form.managerId} onChange={(e) => set('managerId', e.target.value)} options={managerOptions} />
          <Input label="Hire Date" type="date" value={form.hireDate} onChange={(e) => set('hireDate', e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Select label="Leave Approval Chain" value={form.chainId} onChange={(e) => set('chainId', e.target.value)} options={chainOptions} />
          <Input label="Birthday" type="date" max={new Date().toISOString().slice(0, 10)} value={form.birthday} onChange={(e) => set('birthday', e.target.value)} />
        </div>
        <div className="border-t border-border pt-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Leave & Identity</p>
          <div className="grid grid-cols-3 gap-4">
            <Input label="Annual Leave (days/yr)" type="number" min="0" value={form.annualLeaveEntitlement} onChange={(e) => set('annualLeaveEntitlement', e.target.value)} placeholder="e.g. 21" />
            <Input label="Office Location" value={form.officeLocation} onChange={(e) => set('officeLocation', e.target.value)} placeholder="e.g. Riyadh" />
            <Input label="Employee ID" value={form.employeeId} onChange={(e) => set('employeeId', e.target.value)} placeholder="e.g. EMP001" />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">An invite email will be sent if the notification email is configured in Settings.</p>
      </div>
    </Modal>
  );
}

function ManageTeamsModal({ teams, onClose, onChanged }) {
  const toast = useToast();
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  const activeTeams = teams.filter((t) => t.isActive !== false);
  const inactiveTeams = teams.filter((t) => t.isActive === false);

  async function handleCreate() {
    const name = newName.trim();
    if (!name) return;
    const slug = name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    setSaving(true);
    try {
      await adminApi.upsertTeam(slug, { displayName: name });
      toast.success(`Team "${name}" created`);
      setNewName('');
      onChanged();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(team) {
    if (!confirm(`Deactivate team "${team.displayName}"?`)) return;
    try {
      await adminApi.deleteTeam(team.rowKey);
      toast.success(`Team "${team.displayName}" deactivated`);
      onChanged();
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function handleReactivate(team) {
    try {
      await adminApi.upsertTeam(team.rowKey, { displayName: team.displayName, isActive: true });
      toast.success(`Team "${team.displayName}" reactivated`);
      onChanged();
    } catch (err) {
      toast.error(err.message);
    }
  }

  return (
    <Modal isOpen={true} onClose={onClose} title="Manage Teams" width="440px">
      <div className="space-y-4">
        {/* Create new */}
        <div className="flex gap-2">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New team name..."
            className="flex-1"
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          />
          <Button size="sm" onClick={handleCreate} disabled={saving || !newName.trim()}>
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {/* Active teams */}
        {activeTeams.length === 0 && inactiveTeams.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4">
            No teams yet. Create one above.
          </p>
        )}
        {activeTeams.length > 0 && (
          <div className="divide-y divide-border rounded-lg border border-border overflow-hidden">
            {activeTeams.map((t) => (
              <div key={t.rowKey} className="flex items-center justify-between px-3 py-2.5">
                <div>
                  <div className="text-sm font-medium text-foreground">{t.displayName}</div>
                  <div className="text-xs text-muted-foreground font-mono">{t.rowKey}</div>
                </div>
                <button
                  onClick={() => handleDelete(t)}
                  className="p-1.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Inactive teams */}
        {inactiveTeams.length > 0 && (
          <div>
            <p className="text-xs text-muted-foreground mb-2">Inactive</p>
            <div className="divide-y divide-border rounded-lg border border-border overflow-hidden opacity-60">
              {inactiveTeams.map((t) => (
                <div key={t.rowKey} className="flex items-center justify-between px-3 py-2">
                  <span className="text-sm text-muted-foreground line-through">{t.displayName}</span>
                  <Button size="sm" variant="ghost" onClick={() => handleReactivate(t)}>
                    Reactivate
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
